import { z } from 'zod'
import { api, createValidatedRequest } from './baseApi'

// Location types
const LocationSchema = z.object({
  locationId: z.string(),
  name: z.string(),
  merchantId: z.string(),
  currency: z.string(),
})

export type Location = z.infer<typeof LocationSchema>

const MerchantLocationItemSchema = z.object({
  locationId: z.string(),
  name: z.string(),
  currency: z.string(),
})

export type MerchantLocationItem = z.infer<typeof MerchantLocationItemSchema>

const MerchantLocationsResponseSchema = z.object({
  merchantId: z.string(),
  merchantName: z.string(),
  locations: z.array(MerchantLocationItemSchema),
})

export type MerchantLocationsResponse = z.infer<typeof MerchantLocationsResponseSchema>

export interface LocationInfo {
  locationId: string;
  name: string;
  currency: string;
}

const ActiveLocationsResponseSchema = z.object({
  activeLocationIds: z.array(z.string()),
  isAllSelected: z.boolean(),
  locationInfos: z.array(z.object({
    locationId: z.string(),
    name: z.string(),
  })),
})

export type ActiveLocationsResponse = z.infer<typeof ActiveLocationsResponseSchema>

const CurrentMerchantUserSchema = z.object({
  userId: z.string(),
  merchantId: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  role: z.string(),
  hasDashboardAccess: z.boolean(),
  /** Every feature flag evaluated for this user (key → on/off). A key missing from the map is off. */
  featureFlags: z.record(z.boolean()).optional(),
})

export type CurrentMerchantUser = z.infer<typeof CurrentMerchantUserSchema>

// Email OTP types
export interface EmailOtpRequestResponse {
  merchantUserOtpId: string;
  expiresAt: string;
}

export interface EmailOtpVerifyRequest {
  merchantUserOtpId: string;
  otpCode: string;
  email: string;
}

export interface EmailOtpVerifyResponse {
  customToken: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

// API functions
export const getMerchantLocation = async () => {
  return createValidatedRequest(
    LocationSchema,
    api.get<Location>('/merchant/location')
  )
}

export const getMerchantLocations = async () => {
  return createValidatedRequest(
    MerchantLocationsResponseSchema,
    api.get<MerchantLocationsResponse>('/merchant/locations')
  )
}

export const getActiveLocations = async () => {
  return createValidatedRequest(
    ActiveLocationsResponseSchema,
    api.get<ActiveLocationsResponse>('/merchant/active-locations')
  )
}

export const getCurrentMerchantUser = async () => {
  return createValidatedRequest(
    CurrentMerchantUserSchema,
    api.get<CurrentMerchantUser>('/merchant/me')
  )
}

export const updateActiveLocations = async (locationIds: string[]) => {
  const response = await api.put<void>(
    '/merchant/active-locations',
    { activeLocationIds: locationIds, isAllSelected: locationIds?.length === 0 }
  )
  return response.data
}

export const merchantApi = {
  requestEmailOtp: async (email: string): Promise<EmailOtpRequestResponse> => {
    const response = await api.post<EmailOtpRequestResponse>(
      '/merchant/otp/request',
      { email }
    );
    return response.data;
  },

  verifyEmailOtp: async (request: EmailOtpVerifyRequest): Promise<EmailOtpVerifyResponse> => {
    const response = await api.post<EmailOtpVerifyResponse>(
      '/merchant/otp/verify',
      request
    );
    return response.data;
  },

  forgotPassword: async (email: string): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>(
      '/merchant/password/forgot',
      { email }
    );
    return response.data;
  },

  confirmPasswordReset: async (resetToken: string): Promise<ForgotPasswordResponse> => {
    const response = await api.post<ForgotPasswordResponse>(
      '/merchant/password/reset-confirmed',
      { resetToken }
    );
    return response.data;
  },
} 